// Largest allowed dimension (px) for uploaded images. Matches the video
// transcode pipeline (functions/index.js downscales video to max 1920px), so
// images and videos render consistently and very large images (e.g. 4K) don't
// break decoding/playback on the Android player.
export const MAX_IMAGE_DIMENSION = 1920

// Pixel size of an image File as { width, height }, or null when it cannot be read
export const getImageSize = (file) => {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(img.width > 0 && img.height > 0 ? { width: img.width, height: img.height } : null)
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(null)
    }
    img.src = objectUrl
  })
}

// Downscale an image File so its longest side is at most `maxDimension`.
// Returns the original file untouched when it's already within bounds, when
// the format isn't a resizable raster (e.g. animated GIFs are left alone to
// preserve animation), or if anything goes wrong (caller falls back to original).
export const resizeImageFile = (file, maxDimension = MAX_IMAGE_DIMENSION) => {
  return new Promise((resolve, reject) => {
    // Only resize JPEG/PNG; leave GIF (animation) and anything else as-is.
    const isResizable = /image\/(jpeg|png)/.test(file.type) || /\.(jpe?g|png)$/i.test(file.name)
    if (!isResizable) {
      resolve(file)
      return
    }

    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      const { width, height } = img
      const longest = Math.max(width, height)

      // Already small enough — keep the original bytes.
      if (!longest || longest <= maxDimension) {
        resolve(file)
        return
      }

      const scale = maxDimension / longest
      const targetWidth = Math.round(width * scale)
      const targetHeight = Math.round(height * scale)

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Canvas not supported for image resizing.'))
        return
      }
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

      // Preserve PNG transparency; otherwise re-encode as JPEG for smaller files.
      const isPng = /image\/png/.test(file.type) || /\.png$/i.test(file.name)
      const mimeType = isPng ? 'image/png' : 'image/jpeg'
      const quality = isPng ? undefined : 0.9

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Image resize failed.'))
          return
        }
        resolve(new File([blob], file.name, { type: mimeType, lastModified: Date.now() }))
      }, mimeType, quality)
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Could not load image for resizing.'))
    }

    img.src = objectUrl
  })
}
